import { Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { isExactlyAlt, isMacPlatform } from "../../platform";

export type EditorKeyboardHintState = {
  toolbarFocused: boolean;
  editorFocused: boolean;
  /** Whether focus arrived in the editor from the keyboard rather than from a click. */
  enteredByKeyboard: boolean;
};

/**
 * The way into the toolbar, named after the key the user actually has: ⌥ on a Mac (printed «alt»
 * on Nordic keyboards, «option» on US ones), Alt everywhere else.
 */
export const toolbarShortcut = () => (isMacPlatform() ? "⌥+F10" : "Alt+F10");

/**
 * The same shortcut as a key test, so the name in the hint and the binding cannot drift apart.
 * Exactly Alt, no other modifier: Shift+F10 opens the context menu, and the rest belong to the
 * browser or the OS.
 */
export const isToolbarShortcut = (event: KeyboardEvent) =>
  isExactlyAlt(event) && event.key === "F10";

/** Lets the editor decide whether the status row has anything to show at all. */
export const hasEditorKeyboardHint = ({ toolbarFocused, editorFocused, enteredByKeyboard }: EditorKeyboardHintState) =>
  toolbarFocused || (editorFocused && enteredByKeyboard);

/**
 * The keys that are not discoverable on their own, shown only to the people who can act on them:
 * while focus sits in the toolbar, and — for the way in — only to someone navigating by keyboard.
 * Mouse users never see it.
 */
export const EditorKeyboardHint = ({ toolbarFocused, editorFocused, enteredByKeyboard }: EditorKeyboardHintState) => {
  const { t } = useTranslation();

  if (toolbarFocused) {
    return (
      <Text fontSize="xs" color="smMuted" mr="auto">
        {t("Arrow keys move between the buttons. Escape returns to the text.")}
      </Text>
    );
  }

  if (!editorFocused || !enteredByKeyboard) return null;

  return (
    <Text fontSize="xs" color="smMuted" mr="auto">
      {t("Press {{shortcut}} for the formatting toolbar", { shortcut: toolbarShortcut() })}
    </Text>
  );
};
